<template>
	<div class="full-width">
		<AppCard>
			<template v-slot:header>
				<slot name="custom-header">
					<div class="header-title-container">
						<h3>{{ title }}</h3>
						<AppInfoButton v-if="infoButton">
							{{ infoButton }}
						</AppInfoButton>
					</div>
					<div class="header-actions-container">
						<slot name="header-extra-actions" />
						<AppButtonIcon v-if="allowDownload" prevent-change color="var(--color-bg-primary-1)" icon-name="icon-download-blue" @click="toggleExportType" />
					</div>
				</slot>
			</template>
			<template v-slot:body>
				<div ref="table-wrapper" :class="{'no-heading': !filters || !filters.length}" class="table-wrapper col-12 centered">
					<slot name="before-table"></slot>
					<AppFilterGroup v-if="filters && filters.length" :filters="filters" @changed-filter="changedFilter" />
					<div class="table">
						<AppTableHeading :main-color="mainColor" :col-multiplier="colMultiplier" @changed-sort="changedSort" :headings="tableHeadings" />
						<AppTableBody :col-multiplier="colMultiplier" :items="items" :fields="tableHeadings" @click-row="e => this.$emit('click-row', e)" >
							<template v-for="heading in tableHeadings" :key="heading.fieldName" v-slot:[heading.slotName]="{item}">
								<slot :name="heading.slotName" :item="item" :heading="heading" />
							</template>
						</AppTableBody>
					</div>
					<div class="row table-footer">
						<slot name="custom-pagination">
							<div>
								<div class="row footer-pages-select">
									<AppSelect
										:values="appSelectValues"
										:selected-index="0"
										@selected="changePageSize"
									/>
									<p v-show="totalItems && totalItems >= currentPageSize">{{ getString('total_x', [totalItems]) }}</p>
								</div>
							</div>
							<AppPaginationButtonsGroup ref="pagination-btns" @changed-page="changePage" :pagesNumber="totalPages" />
						</slot>
					</div>
				</div>
			</template>


		</AppCard>

		<AppPopup
			ref="export-popup"
			:z-index="exportPopupZIndex"
			:title="getString('export_type_title')"
			:buttons="exportButtons"/>
	</div>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppFilterGroup from "@/shared/components/AppFilterGroup.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppPaginationButtonsGroup from "@/shared/components/AppPaginationButtonsGroup.vue";
import AppTableHeading from "@/shared/components/AppTableHeading.vue";
import AppTableBody from "@/shared/components/AppTableBody.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

import {useLanguageStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppInfoButton from "@/shared/components/AppInfoButton.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString}
	},
	name: "AppTable",
	emits: ["changed-filter", "changed-page", "changed-page-size", "changed-sort", "changed-data", "failed-fetch", "export", "click-row"],
	components: {AppButtonIcon, AppButton, AppFilterGroup, AppCard, AppSelect, AppTableHeading, AppTableBody, AppPaginationButtonsGroup, AppPopup, AppInfoButton},
	data() {
		return {
			queuedRequest: null,
			loading: false,
			currentFilters: {},
			currentOffset: 0,
			currentSortField: this.initialSortField ? this.initialSortField : '',
			currentSortAsc: this.initialSortAsc ? this.initialSortAsc : false,
			currentPageSize: 10,
			tableWidth: 0,
			exportButtons: [
				{
					text: this.getString("export_type_csv"),
					callback: () => {
						this.$emit("export", "csv");
						this.$refs['export-popup'].toggleStatus(false);
					}
				},
				{
					text: this.getString("export_type_excel"),
					callback: () => {
						this.$emit("export", "xlsx");
						this.$refs['export-popup'].toggleStatus(false);
					}
				}
			]
		}
	},
	props: {
		//function called when: page is changed, items per page are changed, filters are changed or to retrieve initial data on mounted
		//parameters: filtersObject and callback
		//filtersObject: its keys are filters property called 'key' (same as tableHeading's fieldName);
		//	 			its values are the selected values formatted (the result of AppFilterGroup.baseFilterProps.getValue)

		fetchFunction: {
			type: Function,
			default: ()=>{},
		},

		//example array of:
		/*{
			type: "checklist",
			key: "status",
			text: this.getString("status"),
			values: [this.getString("canceled"), this.getString("succeeded"), this.getString("refunded"), this.getString("partially_refunded")],
			keysArray: ['canceled', 'succeeded', 'refunded', 'partially_refunded'],
			selectedIndexes: [],
			active: false,
			finalValue: '',
		},*/
		//type: one of [amount, checklist, date, text] (AppFilterGroup.baseFilterProps)
		//key: fieldName
		//values: only relevant for checklists filters
		//keysArray: only relevant for checklists. this one contains the keys of the above values
		//selectedIndexes: only relevant for checklists, is an array of checked indexes of the checklist
		//active: indicates whether the filter currently has a selected value
		//finalValue: resulting string that replaces filter.text in the interface
		filters: {
			type: Array,
			default: [],
			validator(value) {
				return value === null || value.length === 0 || value.every(item => item.text && item.type);
			},
		},
		//object like fieldName => filterValue (es. {type: ["connect_order_pos", 'pos_order'], status: 'pending', ...})
		hiddenFilters: {
			type: Object,
			default: {},
			validator(hiddenFilters) {
				return hiddenFilters === null || Object.keys(hiddenFilters).length === 0 || Object.keys(hiddenFilters).length;
			},
		},
		//Array containing headings objects
		//example of heading object: {text: this.getString('date')(String), cols: 1(Number), sortable: true(Bool), fieldName: 'date'(String)}
		//cols: 1-12, flexbox-like size. it becomes scrollable if the sum of all heading cols is greater than 12.
		//fieldName: single item property name (i.e. items=[{date:..., name:..., value:...}, ... ] fieldName should be one of [date, name, value] and is relevant for filtering, sorting and display in general)
		tableHeadings: {
			type: Array,
			required: true,
			validator(value) {
				return !value || value.every(item => item.text && item.cols && item.fieldName);
			},
		},
		title: {type: String, default: ''},
		mainColor: {type: String, default: 'rgb(7, 82, 163)'},
		//for pagination purposes
		totalItems: {type: Number, default: 0},
		items: {
			type: Array,
			default: [],
		},
		appSelectValues: {
			type: Array,
			default: ['10', '25', '50', '100'],
		},
		initialSortField: {type: String, default: ''},
		initialSortAsc: {type: Boolean, default: false},
		allowDownload: {type: Boolean, default: false},
		noExportTypePopup: {type: Boolean, default: false},
		exportPopupZIndex: {type: Number, default: null},
		infoButton: {
			type: String,
			required: false,
			default: null
		}
	},
	computed: {
		totalPages() {
			return Math.ceil(this.totalItems / this.currentPageSize);
		},
		totalCols() {
			let total = 0;
			this.tableHeadings.forEach(item => total += item.cols || 1);
			return total;
		},
		colMultiplier() {
			let returningMultiplier = 1;
			if(this.tableWidth > 900) {
				returningMultiplier = 1;
			} else if(this.tableWidth > 600) {
				returningMultiplier = 1.5;
			} else if(this.tableWidth > 500) {
				returningMultiplier = 2;
			} else if(this.tableWidth > 300) {
				returningMultiplier = 3;
			} else {
				returningMultiplier = 6;
			}
			let expandMultiplier = 1;
			if (this.totalCols * returningMultiplier < 12) {
				expandMultiplier = 12/(this.totalCols * returningMultiplier);
			} else if(this.totalCols * returningMultiplier > 12) {
				let currentSpace = 0;
				for(let i in this.tableHeadings) {
					let item = this.tableHeadings[i];
					let addingSpace = (item.cols || 1) * returningMultiplier;
					if(currentSpace + addingSpace > 12 && currentSpace < 12 && currentSpace > 0) {
						expandMultiplier = 12/currentSpace;
						break;
					}
					currentSpace += addingSpace;
				}
			}
			return returningMultiplier * expandMultiplier;
		},
	},
	methods: {
		changedFilter(newFilters) {
			if(!newFilters && (!this.currentFilters || !Object.keys(this.currentFilters).length)) { return; }
			let shouldFetch = !newFilters;
			let checkingCurrentFilters = this.currentFilters || {};
			if(!shouldFetch) {
				shouldFetch = JSON.stringify(newFilters) !== JSON.stringify(checkingCurrentFilters);
			}
			if(shouldFetch) {
				this.currentOffset = 0;
				this.currentFilters = newFilters || {};
				this.$emit('changed-filter', newFilters);
				this.fetchData();
			}
		},
		changedSort(newSortField, newSortAsc) {
			this.currentOffset = 0;
			this.currentSortField = newSortField;
			this.currentSortAsc = !!newSortAsc;
			this.$emit('changed-sort', newSortField, newSortAsc);
			this.fetchData();
		},
		changePage(newPage) {
			let newOffset = (newPage - 1) * this.currentPageSize;
			if(this.currentOffset !== newOffset) {
				this.currentOffset = (newPage - 1) * this.currentPageSize;
				this.$emit('changed-page', newPage);
				this.fetchData();
			}
		},
		changePageSize(index, value){
			if(this.currentPageSize !== Number(value)) {
				this.currentOffset = 0;
				this.currentPageSize = Number(value);
				this.$emit('changed-page-size', value);
				this.fetchData();
			}
		},
		fetch() {
			this.loading = true;
			this.currentFilters = this.currentFilters || {};
			let filtersObj = Object.assign({}, this.currentFilters);
			filtersObj.offset = this.currentOffset;
			filtersObj.page_size = this.currentPageSize;
			if(this.currentSortField) {
				filtersObj.sort = this.currentSortField;
				filtersObj.sort_dir = this.currentSortAsc ? 'ASC' : 'DESC';
			}
			if(this.$refs['pagination-btns']) {
				this.$refs['pagination-btns'].activePage = Math.ceil(this.currentOffset / this.currentPageSize) + 1;
			}
			if(this.hiddenFilters && Object.keys(this.hiddenFilters).length) {
				for(let fieldName in this.hiddenFilters) {
					filtersObj[fieldName] = this.hiddenFilters[fieldName];
				}
			}
			this.fetchFunction(filtersObj, (success, details) => {
				if(this.queuedRequest) {
					this.queuedRequest();
					this.queuedRequest = null;
				} else {
					this.loading = false;
					if(!success) {
						this.$emit("failed-fetch", details)
					}
				}
				if(success) {
					this.$emit("changed-data", this.items)
				}
			})
		},
		fetchData() {
			if (this.loading) {
				this.queuedRequest = this.fetch;
			} else {
				this.fetch();
			}
		},
		toggleExportType() {
			if(!this.noExportTypePopup) {
				this.$refs['export-popup'].toggleStatus(true);
			}
			else {
				this.$emit("export", null);
			}
		},
		initTableWidth() {
			this.tableWidth = this.$refs['table-wrapper'] ? this.$refs['table-wrapper'].offsetWidth : 0;
		}
	},
	mounted() {
		this.initTableWidth();
		window.addEventListener('resize', this.initTableWidth);
		this.fetchData();
	},
	beforeUnmount() {
		window.removeEventListener('resize', this.initTableWidth);
	}
}
</script>

<style lang="scss" scoped>
.header-title-container {
	display: flex;
	align-items: center;
	gap: 5px;
}

.header-actions-container {
	display: flex;
	align-items: center;
	gap: 5px;
}

h3 {
	font-size: 18px;
	color: var(--txt-0);
}
.table-wrapper {
	padding-top: 10px;
	&.no-heading {
		padding-top: 0;

		.table {
			margin-top: 0;
		}
	}

	.table-title {
		text-align: left;
		width: 100%;
		margin-bottom: 10px;
	}
	.table {
		@include smallScrollbar;
		overflow-x: auto;
		width: 100%;
		margin-top: 10px;

		scrollbar-width: thin;
		scrollbar-color: var(--color-alpha-4);
		&::-webkit-scrollbar {
			width: 4px;
			height: 4px;
			background-color: transparent;
		}
		&::-webkit-scrollbar-thumb {
			background-color: var(--color-alpha-4);
			border-radius: 10px;
			width: 4px;
			height: 4px;
		}
	}
	.table-footer {
		padding: 5px;
		align-items: center;
		justify-content: space-between;
		border-radius: 0 0 13px 13px;
		background-color: var(--color-bg-primary-2);

		.footer-pages-select {
			align-items: center;
			justify-content: flex-start;

			p {
				padding-left: 10px;
				font-size: 12px;
				max-width: 100px;
			}
		}
	}
	.select-wrapper {
		order: 0;
		max-width: 70px;
		min-width: 70px;

		&:deep(.select-title) {
			height: 32px;
		}
	}
	.pagination-buttons-wrapper {
		max-width: calc(100% - 170px);
	}
	@media (max-width: 400px) {
		.footer-pages-select {
			order: 2;
		}
		.pagination-buttons-wrapper {
			order: 1;
			width: 100%;
			max-width: 100%;
			justify-content: flex-end;
			margin: 5px 0;
		}
	}
}
.card-container {
	:deep(.card-body) {
		padding: 0 !important;
	}
}
.full-width {
	width: 100%;
}
</style>